import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import * as THREE from 'three';
import useEditorStore from './useEditorStore';
import useFinalizationDataStore from './useFinalizationDataStore';

const DEFAULT_THICKNESS_VALUE = 3.0; // in mm
const DEFAULT_OFFSET_VALUE = 3.0; // in mm

const useThicknessStore = create(
  devtools((set, get) => ({
    // Thickness Tool
    thickness: {
      enabled: true,
      value: DEFAULT_THICKNESS_VALUE, // This will be used as bottom thickness for mould, or single thickness for others
      topValue: DEFAULT_THICKNESS_VALUE, // Additional top thickness for mould socket type
      offset: DEFAULT_OFFSET_VALUE, // Offset value for non-mould socket types (0-10 mm)
      unit: 'mm',
      previewVisible: true,
      outerSurfaceSmoothing: {
        enabled: true,
        iterations: 6 // Number of Laplacian smoothing iterations for outer surfaces
      },
      rimBoundarySmoothing: {
        enabled: true,
        smoothnessLength: 1.5, // Scene units (cm) - extent of smooth transition from boundary
        easingProfile: 'ease-out-sine', // Easing function for smooth curve
        applyToInnerSurface: true, // Apply smoothing to inner surface boundaries too
        minimumThickness: 1.0 // Absolute minimum thickness in mm at boundary (clamped to not exceed main thickness)
      }
    },

    // Preview mesh management
    previewMesh: null,
    isThicknessApplied: false,
    isThicknessAppliedFromDraft: false,

    // Actions
    setThickness: (data) => set({ thickness: { ...get().thickness, ...data } }),
    setTopThickness: (topValue) => set({ thickness: { ...get().thickness, topValue } }),
    setOffset: (offset) => set({ thickness: { ...get().thickness, offset } }),
    setOuterSurfaceSmoothing: (smoothingData) => set({
      thickness: {
        ...get().thickness,
        outerSurfaceSmoothing: { ...get().thickness.outerSurfaceSmoothing, ...smoothingData }
      }
    }),
    setPreviewMesh: (mesh) => set({ previewMesh: mesh }),
    setIsThicknessApplied: (applied) => set({ isThicknessApplied: applied }),
    setIsThicknessAppliedFromDraft: (fromDraft) => set({ isThicknessAppliedFromDraft: fromDraft }),

    // Command creation for tool registry
    createThicknessCommand: () => {
      const { thickness, previewMesh } = get();
      const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();
      const originalMaterial = sceneRefs.material;

      if (!previewMesh || !thickness?.enabled || !(parseFloat(thickness?.value || 0) > 0)) {
        console.warn('No valid thickness to apply');
        return null;
      }

      const originalMesh = sceneRefs.shadedMesh;
      const originalGeometry = originalMesh.geometry;
      const originalMatrix = originalMesh.matrix.clone();
      const originalThickness = JSON.parse(JSON.stringify(thickness));

      // Clone geometry from preview (finalized result)
      const cloneGeom = new THREE.BufferGeometry().copy(previewMesh.geometry);
      const cloneMatrix = new THREE.Matrix4().copy(previewMesh.matrix);

      return {
        type: 'APPLY_THICKNESS',
        execute: () => {
          // Remove preview visual
          const latest = get().previewMesh;
          if (latest && latest.isMesh) {
            latest.parent?.remove(latest);
            latest.geometry?.dispose?.();
            latest.material?.dispose?.();
          }

          // Create new mesh with thickness applied
          let finalGeometry = cloneGeom;
          const scene = sceneRefs.scene;

          // Handle top mesh + top lid merging for mould mode
          if (scene) {
            const parts = [];
            // Base/bottom shell (always first)
            parts.push({ geometry: cloneGeom, name: 'bottom' });

            // Optional top side-walls preview
            const topMeshPreview = scene.getObjectByName('TopMeshPreview');
            if (topMeshPreview && topMeshPreview.isMesh && topMeshPreview.visible) {
              parts.push({ geometry: topMeshPreview.geometry.clone(), name: 'topWalls', dispose: () => {
                topMeshPreview.parent?.remove(topMeshPreview);
                topMeshPreview.geometry?.dispose?.();
                topMeshPreview.material?.dispose?.();
              }});
            }

            // Optional top lid preview
            const topLidPreview = scene.getObjectByName('TopLidPreview');
            if (topLidPreview && topLidPreview.isMesh && topLidPreview.visible) {
              parts.push({ geometry: topLidPreview.geometry.clone(), name: 'topLid', dispose: () => {
                topLidPreview.parent?.remove(topLidPreview);
                topLidPreview.geometry?.dispose?.();
                topLidPreview.material?.dispose?.();
              }});
            }

            if (parts.length > 1) {
              const mergedGeometry = new THREE.BufferGeometry();

              // Calculate total sizes
              let totalPositionCount = 0;
              let totalIndexCount = 0;
              const hasIndices = parts.every(p => !!p.geometry.index);
              parts.forEach(p => {
                totalPositionCount += p.geometry.attributes.position.array.length;
                if (p.geometry.index) totalIndexCount += p.geometry.index.array.length;
                else if (!hasIndices) {
                  // If any part is non-indexed and mixing, we'll skip index merge and keep non-indexed
                }
              });

              // Merge positions
              const totalPositions = new Float32Array(totalPositionCount);
              let posOffset = 0;
              const vertexOffsets = [];
              parts.forEach(p => {
                const arr = p.geometry.attributes.position.array;
                totalPositions.set(arr, posOffset);
                vertexOffsets.push(posOffset / 3);
                posOffset += arr.length;
              });
              mergedGeometry.setAttribute('position', new THREE.BufferAttribute(totalPositions, 3));

              // Merge indices when available
              if (hasIndices) {
                const totalIndices = new Uint32Array(totalIndexCount);
                let idxOffset = 0;
                parts.forEach((p, i) => {
                  const idxArr = p.geometry.index.array;
                  const vtxOffset = vertexOffsets[i];
                  for (let k = 0; k < idxArr.length; k++) {
                    totalIndices[idxOffset + k] = idxArr[k] + vtxOffset;
                  }
                  idxOffset += idxArr.length;
                });
                mergedGeometry.setIndex(new THREE.BufferAttribute(totalIndices, 1));
              }

              // Merge vertex colors if any
              const bottomHasColors = !!cloneGeom.attributes.color;
              const anyHasColors = parts.some(p => !!p.geometry.attributes.color);
              if (bottomHasColors || anyHasColors) {
                // Compute average bottom color for filling parts without colors
                let avg = [1, 1, 1];
                if (bottomHasColors) {
                  const bc = cloneGeom.attributes.color.array;
                  let r = 0, g = 0, b = 0;
                  const cnt = bc.length / 3;
                  for (let i = 0; i < cnt; i++) {
                    r += bc[i * 3];
                    g += bc[i * 3 + 1];
                    b += bc[i * 3 + 2];
                  }
                  avg = [r / cnt, g / cnt, b / cnt];
                }

                const totalColors = new Float32Array(totalPositions.length);
                let colOffset = 0;
                parts.forEach(p => {
                  const pa = p.geometry.attributes.position.array;
                  const ca = p.geometry.attributes.color ? p.geometry.attributes.color.array : null;
                  if (ca) {
                    totalColors.set(ca, colOffset);
                    colOffset += ca.length;
                  } else {
                    // Fill with average
                    const vCount = pa.length / 3;
                    for (let i = 0; i < vCount; i++) {
                      const off = colOffset + i * 3;
                      totalColors[off] = avg[0];
                      totalColors[off + 1] = avg[1];
                      totalColors[off + 2] = avg[2];
                    }
                    colOffset += vCount * 3;
                  }
                });
                mergedGeometry.setAttribute('color', new THREE.BufferAttribute(totalColors, 3));
              }

              // Compute normals and bounds for the merged geometry
              mergedGeometry.computeVertexNormals();
              mergedGeometry.computeBoundingSphere();
              mergedGeometry.computeBoundingBox();

              finalGeometry = mergedGeometry;

              // Dispose previews we cloned
              parts.forEach(p => p.dispose && p.dispose());

              console.log('Merged bottom, top walls and top lid for mould mode');
            }
          }

          const newMesh = new THREE.Mesh(finalGeometry, originalMaterial);
          newMesh.matrix.copy(cloneMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          newMesh.matrix.decompose(newMesh.position, newMesh.quaternion, newMesh.scale);
          newMesh.matrixAutoUpdate = true;
          newMesh.name = originalMesh.name;
          replaceShadedMesh(newMesh);

          // Clean up preview mesh if it still exists
          if (previewMesh) {
            previewMesh.parent?.remove(previewMesh);
            previewMesh.geometry?.dispose?.();
            previewMesh.material?.dispose?.();
          }

          // Clean up any remaining top mesh solid attached to the original mesh
          const originalChildren = originalMesh.children.slice();
          originalChildren.forEach(child => {
            if (child.name === 'TopMeshSolid') {
              originalMesh.remove(child);
              child.geometry?.dispose?.();
              // Don't dispose material as it's shared
            }
            if (child.name === 'TopLidSolid') {
              originalMesh.remove(child);
              child.geometry?.dispose?.();
              // Don't dispose material as it's shared
            }
          });

          // Clean up any scene-level top mesh previews
          if (scene) {
            const topMeshPreviewInScene = scene.getObjectByName('TopMeshPreview');
            if (topMeshPreviewInScene) {
              scene.remove(topMeshPreviewInScene);
              topMeshPreviewInScene.geometry?.dispose?.();
              topMeshPreviewInScene.material?.dispose?.();
            }
            const topLidPreviewInScene = scene.getObjectByName('TopLidPreview');
            if (topLidPreviewInScene) {
              scene.remove(topLidPreviewInScene);
              topLidPreviewInScene.geometry?.dispose?.();
              topLidPreviewInScene.material?.dispose?.();
            }
          }

          // Save to finalization store
          useFinalizationDataStore.getState().saveThickness(
            originalThickness.value,
            originalThickness.topValue,
            originalThickness.unit
          );

          // Mark as applied and clear preview
          set({
            isThicknessApplied: true,
            previewMesh: null,
            thickness: { ...thickness, enabled: false }
          });

          console.log('Thickness applied successfully');
        },
        undo: () => {
          const restoredMesh = new THREE.Mesh(originalGeometry, originalMaterial);
          restoredMesh.matrix.copy(originalMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          restoredMesh.matrix.decompose(restoredMesh.position, restoredMesh.quaternion, restoredMesh.scale);
          restoredMesh.matrixAutoUpdate = true;
          replaceShadedMesh(restoredMesh);
          set({
            isThicknessApplied: false,
            thickness: originalThickness
          });
        }
      };
    },

    setCurrentThicknessCommand: (command) => set({ currentThicknessCommand: command }),

    // Reset all data
    reset: () => set({
      thickness: {
        enabled: true,
        value: DEFAULT_THICKNESS_VALUE,
        topValue: DEFAULT_THICKNESS_VALUE,
        offset: DEFAULT_OFFSET_VALUE,
        unit: 'mm',
        previewVisible: false,
        outerSurfaceSmoothing: { enabled: false, iterations: 3 },
        rimBoundarySmoothing: { enabled: true, smoothnessLength: 1.5, easingProfile: 'ease-out-sine', applyToInnerSurface: true, minimumThickness: 1.0 }
      },
      previewMesh: null,
      isThicknessApplied: false,
      currentThicknessCommand: null
    })
  }))
);

export default useThicknessStore;